<!--
// app/components/pages/mercato/simulator/MercatoSquadTab.vue
// @component MercatoSquadTab - Displays current squad with header stats and sell functionality
-->
<script setup lang="ts">
import type { MercatoTeam } from "~~/src/mercato/domain/models/MercatoTeam";
import type { MercatoPlayer } from "~~/src/mercato/domain/models/MercatoPlayer";
import { setAliasUrl } from "~~/src/Shared/utils";
import MercatoPlayerCard from "./MercatoPlayerCard.vue";
import MercatoPlayerSlot from "./MercatoPlayerSlot.vue";

type Props = {
  team: MercatoTeam;
  squad: MercatoPlayer[];
  maxPlayers: number;
};

const props = defineProps<Props>();

const emit = defineEmits<{
  sellPlayer: [player: MercatoPlayer];
}>();

const { $t, $tn } = useI18n();

// Calculate total market value
const totalMarketValue = computed(() => {
  return props.squad.reduce((sum, player) => {
    if (player.marketValue === "free") return sum;
    return sum + Number(player.marketValue);
  }, 0);
});

// Generate empty slots
const emptySlots = computed(() => {
  const emptySlotsCount = props.maxPlayers - props.squad.length;
  return Array.from({ length: emptySlotsCount }, (_, i) => i + props.squad.length + 1);
});

const handleSellPlayer = (player: MercatoPlayer) => {
  emit("sellPlayer", player);
};
</script>

<template>
  <div class="mercato-squad-tab">
    <!-- Header with Team Info -->
    <div class="mercato-squad-tab__header">
      <div class="flex items-center gap-4 mb-4">
        <NuxtImg
          :src="setAliasUrl(team.logo.url)"
          :alt="team.completeName"
          width="48"
          height="48"
          sizes="48px"
          class="rounded-lg"
        />
        <div>
          <h3 class="text-xl font-bold">{{ team.completeName }}</h3>
          <p class="text-gray-400 text-sm">
            {{ $t("mercatoSimulator.squad.playerCount", { count: squad.length, max: maxPlayers }) }}
          </p>
        </div>
      </div>

      <!-- Total Market Value -->
      <div class="mercato-squad-tab__total-value">
        <span class="text-gray-400">{{ $t("mercatoSimulator.squad.totalValue") }}:</span>
        <span class="text-mercato font-bold text-lg ml-2">{{ $tn(totalMarketValue) }} M</span>
      </div>
    </div>

    <!-- Squad Grid -->
    <div class="mercato-squad-tab__grid">
      <!-- Current Squad Players -->
      <MercatoPlayerCard
        v-for="player in squad"
        :key="player.id"
        :player="player"
        mode="sell"
        @sell="handleSellPlayer"
      />

      <!-- Empty Slots -->
      <MercatoPlayerSlot v-for="slotNum in emptySlots" :key="`slot-${slotNum}`" :slot-number="slotNum" />
    </div>
  </div>
</template>

<style scoped>
.mercato-squad-tab {
  @apply flex flex-col gap-4;
}

.mercato-squad-tab__header {
  @apply bg-gray-900 rounded-lg p-4 border border-gray-700;
}

.mercato-squad-tab__total-value {
  @apply flex items-center justify-start text-sm border-t border-gray-700 pt-3 mt-3;
}

.mercato-squad-tab__grid {
  @apply grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[500px] overflow-y-auto p-1;
}
</style>
